import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { apiService } from '../../services/api.service';
import {
  cancelSubscription,
  createCheckoutSession,
  createPortalSession,
  getWorkspacePlan,
  listSeatInvitations,
  resumeSubscription,
  revokeSeatInvitation,
} from '../../services/api/billing.api';
import { queryKeys } from '../../lib/queryClient';
import { useWorkspaceId } from '../../contexts/WorkspaceContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNamespaceSocket } from '../../contexts/SocketContext';
import { safeAnalytics } from '../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../constants/analytics';
import { BillingApiError } from '../../errors/BillingApiError';

export function useWorkspacePlan() {
  const workspaceId = useWorkspaceId();
  const { user, bootLoading, actionLoading } = useAuth();
  const authLoading = bootLoading || actionLoading;

  return useQuery({
    queryKey: queryKeys.billing.plan(workspaceId || ''),
    queryFn: async () => {
      const response = await getWorkspacePlan(apiService);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to fetch billing plan');
      }
      return response.data;
    },
    // Wait for both workspaceId (from localStorage) AND the auth token to
    // be in place. On a hard refresh, workspaceId hydrates first from
    // localStorage while Firebase is still restoring the token; firing
    // without the token returns 401, which the api.service interceptor
    // treats as session loss and redirects to /auth.
    enabled: !!workspaceId && !!user && !authLoading,
    staleTime: 5 * 60 * 1000, // Billing data changes infrequently
  });
}

/**
 * Real-time billing-plan refresh. Listens on the /notifications
 * socket for `billing:plan-updated` events scoped to the current
 * workspace and invalidates the cached plan query so the BillingTab
 * (and any other consumer) refetches without polling.
 *
 * Mount near the app root — same shape as useNotificationSocket.
 * Idempotent: multiple mounts attach independent listeners but each
 * only fires invalidation once per event.
 */
export function useBillingRealtime(): void {
  const socket = useNamespaceSocket('notifications');
  const queryClient = useQueryClient();
  const workspaceId = useWorkspaceId();

  useEffect(() => {
    if (!socket || !workspaceId) return;
    const handler = () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.plan(workspaceId) });
    };
    socket.on('billing:plan-updated', handler);
    return () => {
      socket.off('billing:plan-updated', handler);
    };
  }, [socket, workspaceId, queryClient]);
}

/**
 * List of pending (PENDING + not-yet-expired) workspace invitations.
 * Powers the Members tab's "Pending invitations" section — admins
 * use this to grab the share link again after the create modal closes
 * and to revoke invites they want to redirect.
 */
export function useWorkspaceInvitations() {
  const workspaceId = useWorkspaceId();
  const { user, bootLoading, actionLoading } = useAuth();
  const authLoading = bootLoading || actionLoading;

  return useQuery({
    queryKey: queryKeys.billing.invitations(workspaceId || ''),
    queryFn: async () => {
      const response = await listSeatInvitations(apiService);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to fetch invitations');
      }
      return response.data;
    },
    enabled: !!workspaceId && !!user && !authLoading,
    staleTime: 30 * 1000,
  });
}

export function useRevokeInvitation() {
  const workspaceId = useWorkspaceId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invitationId: string) => {
      const response = await revokeSeatInvitation(apiService, invitationId);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to revoke invitation');
      }
      return response.data;
    },
    onSuccess: () => {
      if (workspaceId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.billing.invitations(workspaceId),
        });
        // paidSeatCount / pendingPaidInvitations change on revoke
        queryClient.invalidateQueries({
          queryKey: queryKeys.billing.plan(workspaceId),
        });
      }
    },
  });
}

export function useCreateCheckoutSession() {
  return useMutation({
    mutationFn: async ({
      tier,
      billingCycle,
      confirmBillingOwnership,
    }: {
      tier: string;
      billingCycle: 'monthly' | 'annual';
      fromGate?: string | null;
      checkoutSource?: 'tier-card' | 'subscription-recovery';
      confirmBillingOwnership?: boolean;
    }) => {
      const response = await createCheckoutSession(
        apiService,
        tier,
        billingCycle,
        confirmBillingOwnership,
      );
      if (!response.success || !response.data) {
        throw new BillingApiError(
          response.error?.message || 'Failed to create checkout session',
          response.error?.code,
        );
      }
      return response.data;
    },
    onSuccess: (data, variables) => {
      safeAnalytics.track(ANALYTICS_EVENTS.BILLING_CHECKOUT_INITIATED, {
        tier: variables.tier,
        billing_cycle: variables.billingCycle,
        from_gate: variables.fromGate ?? null,
        checkout_source: variables.checkoutSource ?? 'tier-card',
      } as never);
      // Redirect to Stripe Checkout
      window.location.href = data.url;
    },
  });
}

/**
 * Opens a Stripe Customer Portal session and redirects the browser to it.
 *
 * The `intent` attribute is required so the BILLING_PORTAL_OPENED PostHog
 * event can power a funnel: tier-card switch → portal view → confirm →
 * subscription.changed webhook. Every entry point into the portal should
 * pass a distinct intent.
 *
 * When `flowType: 'switch-plan'` is supplied, the backend builds a
 * Stripe `flow_data` deep-link so the portal opens directly on the
 * confirmation page for that tier + cycle. Stripe handles SCA, the
 * exact-amount disclosure, and the charge on their hosted page.
 */
export function useCreatePortalSession() {
  return useMutation({
    mutationFn: async (args: {
      intent: 'manage' | 'reactivate' | 'switch-plan' | 'payment-method-update';
      flowType?: 'switch-plan' | 'manage' | 'reactivate' | 'payment-method-update';
      tier?: 'PRO' | 'TEAM';
      billingCycle?: 'monthly' | 'annual';
      confirmBillingOwnership?: boolean;
    }) => {
      const hasBody =
        args.flowType || args.tier || args.billingCycle || args.confirmBillingOwnership;
      const body = hasBody
        ? {
            flowType: args.flowType,
            tier: args.tier,
            billingCycle: args.billingCycle,
            ...(args.confirmBillingOwnership !== undefined && {
              confirmBillingOwnership: args.confirmBillingOwnership,
            }),
          }
        : undefined;
      const response = await createPortalSession(apiService, body);
      if (!response.success || !response.data) {
        throw new BillingApiError(
          response.error?.message || 'Failed to create portal session',
          response.error?.code,
        );
      }
      return { ...response.data, intent: args.intent };
    },
    onSuccess: (data) => {
      safeAnalytics.track(ANALYTICS_EVENTS.BILLING_PORTAL_OPENED, {
        intent: data.intent,
      } as never);
      // Redirect to Stripe Customer Portal (or deep-linked flow)
      window.location.href = data.url;
    },
  });
}

export function useCancelSubscription() {
  const workspaceId = useWorkspaceId();
  const queryClient = useQueryClient();
  const { data: plan } = useWorkspacePlan();

  return useMutation({
    mutationFn: async () => {
      const response = await cancelSubscription(apiService);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to cancel subscription');
      }
      return response.data;
    },
    onSuccess: () => {
      safeAnalytics.track(ANALYTICS_EVENTS.BILLING_DOWNGRADE_INITIATED, {
        from_tier: plan?.tier ?? 'unknown',
        to_tier: 'FREE',
        from_cycle: plan?.billingCycle?.toLowerCase() ?? null,
      } as never);
      if (workspaceId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.billing.plan(workspaceId) });
      }
    },
  });
}

/**
 * Un-schedule a pending cancellation. Server calls
 * `stripe.subscriptions.update(id, { cancel_at_period_end: false, cancel_at: null })`
 * and writes matching fields in our DB. No card charge, no Portal bounce.
 */
export function useResumeSubscription() {
  const workspaceId = useWorkspaceId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await resumeSubscription(apiService);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to reactivate subscription');
      }
      return response.data;
    },
    onSuccess: () => {
      safeAnalytics.track(ANALYTICS_EVENTS.BILLING_SUBSCRIPTION_REACTIVATED, {} as never);
      if (workspaceId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.billing.plan(workspaceId) });
      }
    },
  });
}
